import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PropTag } from '@/types/props';
import { HolidayType } from '@/types/sequence';

interface SequencePropsProps {
  props: PropTag[];
  currentHoliday?: HolidayType;
}

const SequenceProps: React.FC<SequencePropsProps> = ({ props, currentHoliday }) => {
  const navigate = useNavigate();

  // Group props by holiday type
  const groupedProps = React.useMemo(() => {
    const universal: PropTag[] = [];
    const holidaySpecific: PropTag[] = [];
    
    props.forEach(prop => {
      if (prop.holidayTypes.length === 0 || 
          (currentHoliday && prop.holidayTypes.includes(currentHoliday))) {
        universal.push(prop);
      } else {
        holidaySpecific.push(prop);
      }
    });
    
    return { universal, holidaySpecific };
  }, [props, currentHoliday]);

  const handlePropClick = (prop: PropTag) => {
    // Navigate to sequences page with prop filter
    // Remove quantity from prop name if it exists
    const propName = prop.name.toLowerCase().replace(/\s+/g, '_');
    navigate(`/sequences?props_used=${propName}`);
  };

  if (!props.length) return null;

  return (
    <div className="sequence-props">
      <h3 className="text-xl font-semibold mb-4">Props Used</h3>
      
      {/* Universal Props */}
      {groupedProps.universal.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {groupedProps.universal.map(prop => (
              <button
                key={prop.id}
                onClick={() => handlePropClick(prop)}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm 
                         bg-purple-100 text-purple-800 hover:bg-purple-200 
                         transition-colors cursor-pointer"
                title={`${prop.quantity || 1}x ${prop.name} - Click to find sequences using this prop`}
              >
                {prop.name}
                {prop.quantity > 1 && (
                  <span className="ml-1 text-purple-600">({prop.quantity})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Holiday-specific Props */}
      {groupedProps.holidaySpecific.length > 0 && (
        <div>
          <div className="flex flex-wrap gap-2">
            {groupedProps.holidaySpecific.map(prop => (
              <button
                key={prop.id}
                onClick={() => handlePropClick(prop)}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm 
                         bg-gray-100 text-gray-800 hover:bg-gray-200 
                         transition-colors cursor-pointer"
                title={`${prop.quantity || 1}x ${prop.name} - ${prop.holidayTypes.join(', ')} - Click to find sequences using this prop`}
              >
                {prop.name}
                {prop.quantity > 1 && (
                  <span className="ml-1 text-gray-600">({prop.quantity})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* SEO-friendly hidden content */}
      <div className="sr-only">
        <h4>Props and Models Used in This Light Sequence:</h4>
        <ul>
          {props.map(prop => (
            <li key={prop.id}>
              {prop.quantity > 1 ? `${prop.quantity}x ` : ''}{prop.name}
              {prop.holidayTypes.length > 0 && ` - Compatible with ${prop.holidayTypes.join(', ')}`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SequenceProps; 